import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  MapPin,
  Zap,
  Wifi,
  WifiOff,
  Activity,
  AlertTriangle,
  Settings,
  Plus,
} from "lucide-react";
import SectionLayout from "../../lib/components/SectionLayout";
import { useFleetData } from "../../lib/context/FleetDataProvider";
import useHierarchy from "../../lib/hooks/useHierarchy";
import { getCompanyDisplayName } from "../../lib/utils/featureAccess";
const STATION_TABS = [
  { to: "/stations", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/stations/manage", label: "Manage Station", icon: Settings },
];

export default function StationSection() {
  const { state: fleetState } = useFleetData();
  const { stations: hierarchyStations } = useHierarchy();

  const stats = useMemo(() => {
    const incoming = Array.isArray(fleetState?.data) ? fleetState.data : [];
    const uniqueSites = new Set();
    let online = 0;
    let charging = 0;
    let faulted = 0;

    incoming.forEach(charger => {
      if (charger.siteId) uniqueSites.add(charger.siteId);
      if (charger.isConnected) online += 1;
      if (charger.isFaulted) faulted += 1;
      if (charger.connectors?.some(cn => cn.connectorStatusType === "CHARGING")) charging += 1;
    });

    const totalSites = uniqueSites.size;
    const total = incoming.length;
    const offline = total - online;

    const stationCount = hierarchyStations.filter(
      station => (station.stationName || station.name || "").toLowerCase() !== "unassigned"
    ).length;

    return {
      stations: stationCount,
      totalSites,
      sitesWithChargers: totalSites,
      sitesNoChargers: 0,
      total,
      online,
      offline,
      charging,
      faulted,
    };
  }, [fleetState?.data, hierarchyStations]);

  const navigate = useNavigate();
  const companyName = getCompanyDisplayName();

  const tabActions = (
    <button className="tab-bar-action-btn" onClick={() => navigate("/stations/create")}>
      <Plus size={14} />
      Create Station
    </button>
  );

  const heroStats = (
    <>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Stations</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <Building2 size={12} className="hero-stat__icon hero-stat__icon--teal" />
            <span className="hero-stat__value">{stats.stations}</span>
          </div>
        </div>
      </div>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Sites</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <MapPin size={12} className="hero-stat__icon hero-stat__icon--purple" />
            <span className="hero-stat__value">{stats.totalSites}</span>
          </div>
        </div>
      </div>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Chargers</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <Zap size={12} className="hero-stat__icon hero-stat__icon--teal" />
            <span className="hero-stat__value">{stats.total}</span>
            <span className="hero-stat__label">Total</span>
          </div>
          <div className="hero-stat__group-item">
            <Wifi size={12} className="hero-stat__icon hero-stat__icon--green" />
            <span className="hero-stat__value">{stats.online}</span>
            <span className="hero-stat__label">Online</span>
          </div>
          <div className="hero-stat__group-item">
            <WifiOff size={12} className="hero-stat__icon hero-stat__icon--red" />
            <span className="hero-stat__value">{stats.offline}</span>
            <span className="hero-stat__label">Offline</span>
          </div>
          <div className="hero-stat__group-item">
            <Activity size={12} className="hero-stat__icon hero-stat__icon--blue" />
            <span className="hero-stat__value">{stats.charging}</span>
            <span className="hero-stat__label">Charging</span>
          </div>
          <div className="hero-stat__group-item">
            <AlertTriangle size={12} className="hero-stat__icon hero-stat__icon--amber" />
            <span className="hero-stat__value">{stats.faulted}</span>
            <span className="hero-stat__label">Faulted</span>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <SectionLayout
      companyName={companyName}
      title="Charging Stations"
      subtitle="Manage stations composed of multiple sites"
      tabs={STATION_TABS}
      tabActions={tabActions}
      heroStats={heroStats}
    />
  );
}
